import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Divider,
  message,
  Space,
  Tag,
  Statistic,
  Row,
  Col,
  Typography,
  Tooltip,
  Alert,
  List,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  ShopOutlined,
  TeamOutlined,
  EnvironmentOutlined,
  CheckCircleOutlined,
  PhoneOutlined,
  UserOutlined,
  DeleteOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import api from "../../services/api";
import LoadingSpinner from "../../components/UI/LoadingSpinner";

const { Title, Text } = Typography;
const { TextArea } = Input;

const AllCompanies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteTargetDetails, setDeleteTargetDetails] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [form] = Form.useForm();

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await api.get("/superadmin/companies");

      if (response.data.success) {
        setCompanies(response.data.data);
      }
    } catch (error) {
      console.error("Fetch companies error:", error);
      message.error("ไม่สามารถโหลดข้อมูลบริษัทได้");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    form.resetFields();
    setSelectedCompany(null);
    setModalVisible(true);
  };

  const handleEdit = (company) => {
    setSelectedCompany(company);
    form.setFieldsValue({
      name: company.name,
      address: company.address,
      phone: company.phone,
      phone_ext: company.phone_ext,
      note: company.note,
    });
    setModalVisible(true);
  };

  const handleUpdate = async (values) => {
    try {
      const response = await api.put(
        `/superadmin/companies/${selectedCompany.id}`,
        values,
      );
      if (response.data.success) {
        message.success("อัปเดตข้อมูลบริษัทสำเร็จ!");
        setModalVisible(false);
        fetchCompanies();
      }
    } catch (error) {
      console.error("Update company error:", error);
      message.error(
        error.response?.data?.message || "อัปเดตข้อมูลบริษัทล้มเหลว",
      );
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (selectedCompany) {
        return handleUpdate(values);
      }

      const response = await api.post("/superadmin/companies", values);

      if (response.data.success) {
        message.success("เพิ่มบริษัทสำเร็จ!");
        setModalVisible(false);
        fetchCompanies();
      }
    } catch (error) {
      console.error("Create company error:", error);
      message.error(
        error.response?.data?.message || "เพิ่มบริษัทล้มเหลว",
      );
    }
  };

  const handleDeleteClick = async (company) => {
    setDeleteTarget(company);
    setDeleteTargetDetails(null);
    setConfirmText("");
    setDeleteModalVisible(true);
    try {
      const response = await api.get(`/superadmin/companies/${company.id}`);
      if (response.data.success) {
        setDeleteTargetDetails(response.data.data);
      }
    } catch (error) {
      console.error("Load company details for delete error:", error);
      message.error("ไม่สามารถโหลดรายละเอียดบริษัทได้");
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteLoading(true);
      const response = await api.delete(`/superadmin/companies/${deleteTarget.id}`);
      if (response.data.success) {
        message.success(`ลบบริษัท "${deleteTarget.name}" สำเร็จ`);
        setDeleteModalVisible(false);
        fetchCompanies();
      }
    } catch (error) {
      console.error("Delete company error:", error);
      message.error(error.response?.data?.message || "ลบบริษัทล้มเหลว");
    } finally {
      setDeleteLoading(false);
    }
  };

  const showCompanyDetails = async (company) => {
    try {
      setSelectedCompany(company);
      const response = await api.get(`/superadmin/companies/${company.id}`);

      if (response.data.success) {
        setCompanyDetails(response.data.data);
        setDetailModalVisible(true);
      }
    } catch (error) {
      console.error("Get company details error:", error);
      message.error("ไม่สามารถโหลดรายละเอียดบริษัทได้");
    }
  };

  const getStatusTag = (company) => {
    const hasUsers = company.user_count > 0;
    const hasCheckpoints = company.checkpoint_count > 0;
    const hasGuards = company.guard_count > 0;

    if (hasUsers && hasCheckpoints && hasGuards) {
      return <Tag color="green">ใช้งานอยู่</Tag>;
    } else if (hasUsers) {
      return <Tag color="orange">ต้องตั้งค่า</Tag>;
    } else {
      return <Tag color="red">ไม่ใช้งาน</Tag>;
    }
  };

  const columns = [
    {
      title: "ชื่อบริษัท",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          {record.address && (
            <div style={{ fontSize: "12px", color: "#999", marginTop: 4 }}>
              {record.address}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "สถานะ",
      key: "status",
      render: (_, record) => getStatusTag(record),
      width: 100,
    },
    {
      title: "ผู้ใช้งาน",
      dataIndex: "non_guard_count",
      key: "non_guard_count",
      render: (count) => (
        <Space>
          <UserOutlined />
          <span>{count}</span>
        </Space>
      ),
      width: 80,
      align: "center",
    },
    {
      title: "รปภ.",
      dataIndex: "guard_count",
      key: "guard_count",
      render: (count) => (
        <Space>
          <TeamOutlined />
          <span>{count}</span>
        </Space>
      ),
      width: 80,
      align: "center",
    },
    {
      title: "จุดตรวจ",
      dataIndex: "checkpoint_count",
      key: "checkpoint_count",
      render: (count) => (
        <Space>
          <EnvironmentOutlined />
          <span>{count}</span>
        </Space>
      ),
      width: 100,
      align: "center",
    },
    {
      title: "ติดต่อ",
      key: "phone",
      render: (_, record) => {
        if (!record.phone) return <Text type="secondary">-</Text>;
        const p = record.phone;
        // 10 digits (mobile): 09X-XXX-XXXX, 9 digits (landline): 0X-XXX-XXXX
        const formatted = p.length === 10
          ? `${p.slice(0, 3)}-${p.slice(3, 6)}-${p.slice(6)}`
          : p.length === 9
            ? `${p.slice(0, 2)}-${p.slice(2, 5)}-${p.slice(5)}`
            : p;
        const display = record.phone_ext
          ? `${formatted} #${record.phone_ext}`
          : formatted;
        return (
          <a
            href={`tel:${record.phone}${record.phone_ext ? "," + record.phone_ext : ""}`}
          >
            <Space>
              <PhoneOutlined />
              {display}
            </Space>
          </a>
        );
      },
      width: 200,
    },
    {
      title: "วันที่สร้าง",
      dataIndex: "created_at",
      key: "created_at",
      render: (date) => new Date(date).toLocaleDateString("th-TH"),
      width: 100,
    },
    {
      title: "จัดการ",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="แก้ไข">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="รายละเอียด">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => showCompanyDetails(record)}
            />
          </Tooltip>
          <Tooltip title="ลบบริษัท">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteClick(record)}
            />
          </Tooltip>
        </Space>
      ),
      width: 140,
    },
  ];

  if (loading) {
    return <LoadingSpinner tip="กำลังโหลดข้อมูลบริษัท..." />;
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            <ShopOutlined /> บริษัททั้งหมด
          </Title>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
            size="large"
          >
            เพิ่มบริษัท
          </Button>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card className="stats-card">
            <Statistic
              title="บริษัททั้งหมด"
              value={companies.length}
              prefix={<ShopOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="stats-card">
            <Statistic
              title="บริษัทที่ใช้งาน"
              value={
                companies.filter(
                  (c) => c.user_count > 0 && c.checkpoint_count > 0,
                ).length
              }
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="stats-card">
            <Statistic
              title="ผู้ใช้งานทั้งหมด"
              value={companies.reduce((sum, c) => sum + c.user_count, 0)}
              prefix={<TeamOutlined />}
              valueStyle={{ color: "#722ed1" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="stats-card">
            <Statistic
              title="จุดตรวจทั้งหมด"
              value={companies.reduce((sum, c) => sum + c.checkpoint_count, 0)}
              prefix={<EnvironmentOutlined />}
              valueStyle={{ color: "#fa8c16" }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Table
          columns={columns}
          dataSource={companies}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} จากทั้งหมด ${total} บริษัท`,
          }}
        />
      </Card>

      {/* Create/Edit Company Modal */}
      <Modal
        title={selectedCompany ? "แก้ไขบริษัท" : "เพิ่มบริษัทใหม่"}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark={false}
        >
          <Form.Item
            name="name"
            label="ชื่อบริษัท"
            rules={[
              { required: true, message: "กรุณาระบุชื่อบริษัท" },
              { min: 2, message: "ชื่อต้องมีอย่างน้อย 2 ตัวอักษร" },
            ]}
          >
            <Input placeholder="ระบุชื่อบริษัท" />
          </Form.Item>

          <Form.Item name="address" label="ที่อยู่">
            <TextArea placeholder="ระบุที่อยู่บริษัท" rows={3} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={16}>
              <Form.Item
                name="phone"
                label="เบอร์โทรศัพท์"
                rules={[
                  {
                    pattern: /^0\d{8,9}$/,
                    message: "เบอร์โทรต้องขึ้นต้นด้วย 0 และมี 9-10 หลัก",
                  },
                ]}
              >
                <Input
                  placeholder="0812345678"
                  maxLength={10}
                  onKeyDown={(e) => {
                    if (e.key.length === 1 && !/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="phone_ext" label="เบอร์ต่อ">
                <Input
                  placeholder="1234"
                  maxLength={10}
                  onKeyDown={(e) => {
                    if (e.key.length === 1 && !/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="note" label="หมายเหตุ">
            <TextArea
              placeholder="บันทึกสำคัญเกี่ยวกับบริษัทนี้"
              rows={3}
              maxLength={1000}
              showCount
            />
          </Form.Item>

          {!selectedCompany && (
            <>
              <Divider>บัญชีเจ้าของ</Divider>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="owner_name"
                    label="ชื่อเจ้าของ"
                    rules={[
                      { required: true, message: "กรุณาระบุชื่อเจ้าของ" },
                    ]}
                  >
                    <Input placeholder="ระบุชื่อเจ้าของ" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="owner_email"
                    label="อีเมลเจ้าของ"
                    rules={[
                      { required: true, message: "กรุณาระบุอีเมลเจ้าของ" },
                      { type: "email", message: "รูปแบบอีเมลไม่ถูกต้อง" },
                    ]}
                  >
                    <Input placeholder="owner@company.com" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="owner_password"
                label="รหัสผ่านเจ้าของ"
                rules={[
                  { required: true, message: "กรุณาระบุรหัสผ่านเจ้าของ" },
                  { min: 6, message: "รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร" },
                ]}
              >
                <Input.Password placeholder="ระบุรหัสผ่าน" />
              </Form.Item>
            </>
          )}

          <Form.Item style={{ textAlign: "right", marginTop: 24 }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>ยกเลิก</Button>
              <Button type="primary" htmlType="submit">
                {selectedCompany ? "บันทึกการเปลี่ยนแปลง" : "สร้างบริษัท"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Delete Company Modal */}
      <Modal
        title={
          <Space>
            <WarningOutlined style={{ color: "#ff4d4f" }} />
            <span style={{ color: "#ff4d4f" }}>ลบบริษัท: {deleteTarget?.name}</span>
          </Space>
        }
        open={deleteModalVisible}
        onCancel={() => setDeleteModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setDeleteModalVisible(false)}>
            ยกเลิก
          </Button>,
          <Button
            key="delete"
            type="primary"
            danger
            icon={<DeleteOutlined />}
            disabled={confirmText !== "ยืนยัน"}
            loading={deleteLoading}
            onClick={handleDeleteConfirm}
          >
            ลบบริษัทถาวร
          </Button>,
        ]}
        width={600}
      >
        <Alert
          type="error"
          showIcon
          icon={<WarningOutlined />}
          message="คำเตือน: การลบนี้ไม่สามารถกู้คืนได้"
          description="ข้อมูลทั้งหมดที่เกี่ยวข้องกับบริษัทนี้จะถูกลบออกจากระบบอย่างถาวร และไม่สามารถกู้กลับมาได้ในทุกกรณี"
          style={{ marginBottom: 16 }}
        />

        {deleteTargetDetails ? (
          <div style={{ marginBottom: 16 }}>
            <p style={{ fontWeight: 600, marginBottom: 8 }}>ข้อมูลต่อไปนี้จะถูกลบทั้งหมด:</p>
            <List
              size="small"
              bordered
              dataSource={[
                {
                  label: "ผู้ใช้งาน",
                  count: deleteTargetDetails.users?.length || 0,
                  items: deleteTargetDetails.users?.map((u) => `${u.name} (${u.role})`),
                  color: "#ff4d4f",
                },
                {
                  label: "รปภ.",
                  count: deleteTargetDetails.guards?.length || 0,
                  items: deleteTargetDetails.guards?.map((g) => g.name),
                  color: "#fa8c16",
                },
                {
                  label: "จุดตรวจ",
                  count: deleteTargetDetails.checkpoints?.length || 0,
                  items: deleteTargetDetails.checkpoints?.map((c) => c.name),
                  color: "#1890ff",
                },
              ]}
              renderItem={(section) => (
                <List.Item>
                  <div style={{ width: "100%" }}>
                    <Space style={{ marginBottom: section.items?.length ? 4 : 0 }}>
                      <Tag color={section.count > 0 ? "red" : "default"}>
                        {section.count} รายการ
                      </Tag>
                      <Text strong>{section.label}</Text>
                    </Space>
                    {section.items?.length > 0 && (
                      <div style={{ paddingLeft: 8, marginTop: 4 }}>
                        {section.items.map((item, i) => (
                          <div key={i} style={{ fontSize: 12, color: "#666", lineHeight: "20px" }}>
                            • {item}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </List.Item>
              )}
            />
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "16px 0", color: "#999" }}>
            กำลังโหลดรายละเอียด...
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <p>
            พิมพ์ <Text strong style={{ color: "#ff4d4f" }}>ยืนยัน</Text> เพื่อยืนยันการลบ:
          </p>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder='พิมพ์ "ยืนยัน" เพื่อดำเนินการต่อ'
            status={confirmText && confirmText !== "ยืนยัน" ? "error" : ""}
          />
        </div>
      </Modal>

      {/* Company Details Modal */}
      <Modal
        title={`รายละเอียดบริษัท - ${selectedCompany?.name}`}
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            ปิด
          </Button>,
        ]}
        width={800}
      >
        {companyDetails && (
          <div>
            {companyDetails.company?.note && (
              <Card size="small" style={{ marginBottom: 16, backgroundColor: '#fffbe6', borderColor: '#ffe58f' }}>
                <Text strong>หมายเหตุ: </Text>
                <Text>{companyDetails.company.note}</Text>
              </Card>
            )}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col span={8}>
                <Card size="small">
                  <Statistic
                    title="ผู้ใช้งาน"
                    value={companyDetails.users.length}
                    prefix={<UserOutlined />}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small">
                  <Statistic
                    title="รปภ."
                    value={companyDetails.guards?.length || 0}
                    prefix={<TeamOutlined />}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small">
                  <Statistic
                    title="จุดตรวจ"
                    value={companyDetails.checkpoints.length}
                    prefix={<EnvironmentOutlined />}
                  />
                </Card>
              </Col>
            </Row>

            <div style={{ marginBottom: 16 }}>
              <Title level={4}><UserOutlined /> ผู้ใช้งาน ({companyDetails.users.length})</Title>
              {companyDetails.users.length > 0 ? companyDetails.users.map((user) => (
                <div
                  key={user.id}
                  style={{
                    padding: "8px 12px",
                    border: "1px solid #f0f0f0",
                    marginBottom: 8,
                    borderRadius: 4,
                  }}
                >
                  <Space>
                    <Text strong>{user.name}</Text>
                    <Tag
                      color={
                        user.role === "owner"
                          ? "red"
                          : user.role === "supervisor"
                            ? "orange"
                            : "blue"
                      }
                    >
                      {user.role.toUpperCase()}
                    </Tag>
                    {user.email && <Text type="secondary">{user.email}</Text>}
                  </Space>
                </div>
              )) : (
                <Text type="secondary">ไม่มีผู้ใช้งาน</Text>
              )}
            </div>

            <div style={{ marginBottom: 16 }}>
              <Title level={4}><TeamOutlined /> รปภ. ({companyDetails.guards?.length || 0})</Title>
              {companyDetails.guards?.length > 0 ? companyDetails.guards.map((guard) => (
                <div
                  key={guard.id}
                  style={{
                    padding: "8px 12px",
                    border: "1px solid #f0f0f0",
                    marginBottom: 8,
                    borderRadius: 4,
                  }}
                >
                  <Space wrap>
                    <Text strong>
                      {guard.name}
                      {guard.employee_id && <Text type="secondary"> ({guard.employee_id})</Text>}
                    </Text>
                    <Tag color="green">GUARD</Tag>
                    {guard.home_company_name && (
                      <Tag color="blue">{guard.home_company_name}</Tag>
                    )}
                    {!!guard.device_locked && (
                      <Tag color="orange">ล็อกอุปกรณ์แล้ว</Tag>
                    )}
                  </Space>
                </div>
              )) : (
                <Text type="secondary">ไม่มี รปภ. ในระบบ</Text>
              )}
            </div>

            <div>
              <Title level={4}>จุดตรวจ</Title>
              {companyDetails.checkpoints.map((checkpoint) => (
                <div
                  key={checkpoint.id}
                  style={{
                    padding: "8px 12px",
                    border: "1px solid #f0f0f0",
                    marginBottom: 8,
                    borderRadius: 4,
                  }}
                >
                  <Space>
                    <EnvironmentOutlined />
                    <Text strong>{checkpoint.name}</Text>
                    <Text type="secondary">
                      {checkpoint.latitude}, {checkpoint.longitude}
                    </Text>
                  </Space>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AllCompanies;
